import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import CornerPanel from '../components/CornerPanel';
import RefreshButton from '../components/RefreshButton';
import { Newspaper, ExternalLink, Radio } from 'lucide-react';
import { cstDateTime } from '../lib/time';

// Pretty-format a Finnhub unix-epoch-seconds timestamp as "4m ago · 08:42 CST"
function relAge(ts) {
  if (!ts) return '—';
  const ms = Number(ts) * 1000;
  const diff = Date.now() - ms;
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${Math.max(mins, 0)}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function News() {
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState('general');
  const [last, setLast] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/news/market');
      setCats(data.categories || []);
      if (!data.categories?.find((c) => c.key === active)) {
        setActive(data.categories?.[0]?.key || 'general');
      }
      setLast(new Date().toISOString());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 5 * 60 * 1000);
    // Safety retry: if cats is still empty after 3s (initial render race), reload once.
    const retry = setTimeout(() => { if (cats.length === 0) load(); }, 3000);
    return () => { clearInterval(iv); clearTimeout(retry); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeCat = cats.find((c) => c.key === active) || { items: [] };

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="font-display tracking-[0.35em] uppercase text-cyan-sys text-sm flex items-center gap-2">
          <Newspaper size={14} /> market news // live feed
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] font-mono text-[var(--txt-secondary)]">
            <Radio size={9} className="inline text-buy animate-pulse mr-1" />
            auto-refresh every 5 min · influences AI reasoning + chat context
          </span>
          <RefreshButton onRefresh={load} lastUpdated={last} testId="news-refresh" />
        </div>
      </div>

      <CornerPanel title="categories" testId="news-tabs">
        <div className="flex flex-wrap gap-2">
          {cats.map((c) => (
            <button
              key={c.key}
              data-testid={`news-tab-${c.key}`}
              onClick={() => setActive(c.key)}
              className={`text-[11px] font-display tracking-[0.3em] uppercase px-4 py-1.5 border transition-colors ${
                active === c.key
                  ? 'bg-cyan-sys text-black border-cyan-sys'
                  : 'text-cyan-sys border-[var(--border-muted)] hover:border-cyan-sys'
              }`}
            >
              {c.label} <span className="ml-2 text-[9px] opacity-70">· {c.items.length}</span>
            </button>
          ))}
        </div>
      </CornerPanel>

      <CornerPanel
        title={activeCat.label || active}
        right={<span className="text-cyan-sys">{activeCat.items.length} stories</span>}
        testId="news-panel"
      >
        {loading && activeCat.items.length === 0 && (
          <div className="text-[var(--txt-muted)] text-[12px]">// fetching feed...</div>
        )}
        {!loading && activeCat.items.length === 0 && (
          <div className="text-[var(--txt-muted)] text-[12px]">// no stories in this category right now.</div>
        )}
        <div className="space-y-3">
          {activeCat.items.map((n) => (
            <a
              key={n.id || n.url}
              href={n.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`news-item-${n.id}`}
              className="block p-3 border border-[var(--border-default)] bg-[var(--bg-panel)] hover:border-cyan-sys hover:bg-[#0d1113] transition-colors group"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest mb-1">
                    <span className="text-cyan-sys">{n.source || '—'}</span>
                    <span className="text-[var(--txt-muted)]">·</span>
                    <span className="text-[var(--txt-secondary)]">{relAge(n.datetime)}</span>
                    {n.related && (
                      <>
                        <span className="text-[var(--txt-muted)]">·</span>
                        <span className="text-hold">{String(n.related).split(',').slice(0, 3).join(', ')}</span>
                      </>
                    )}
                  </div>
                  <div className="font-display text-[14px] text-[var(--txt-primary)] group-hover:text-cyan-sys leading-snug">
                    {n.headline}
                  </div>
                  {n.summary && (
                    <div className="mt-2 text-[12px] font-mono text-[var(--txt-secondary)] leading-relaxed line-clamp-3">
                      {n.summary}
                    </div>
                  )}
                  <div className="mt-2 text-[9px] font-mono text-[var(--txt-muted)]">
                    {n.datetime ? cstDateTime(new Date(Number(n.datetime) * 1000)) : ''}
                  </div>
                </div>
                {n.image && (
                  <img
                    src={n.image}
                    alt=""
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    className="w-32 h-20 object-cover border border-[var(--border-default)] hidden md:block"
                  />
                )}
                <ExternalLink size={12} className="text-[var(--txt-muted)] group-hover:text-cyan-sys flex-shrink-0" />
              </div>
            </a>
          ))}
        </div>
      </CornerPanel>
    </div>
  );
}
